import {Document, Page, StyleSheet, Text, View, PDFViewer} from "@react-pdf/renderer";
import {Table, TD, TH, TR} from "@ag-media/react-pdf-table";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import MedicalHistoryPdf from "../medical-history/MedicalHistoryPdf";
import moment from "moment-timezone";
import {CommonService} from "../../../shared/services";
import React from "react";
import SurgicalHistoryPdf from "../surgical-history/SurgicalHistoryPdf";


const styles = StyleSheet.create({
    page: {
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 24,
        paddingBottom: 50, // Add padding at the bottom to make room for footer
        position: 'relative', // Important for positioning child elements
    },
    contentContainer: {
        flexGrow: 1,
    },
    header: {
        backgroundColor: "#000000",
        color: "white",
        paddingLeft: 12,
        paddingTop: 5,
        paddingBottom: 5,
        fontSize: 12,
        marginBottom: 16
    },
    heading: {
        fontSize: 10,
        marginLeft: 12,
        textDecoration: "underline",
        textTransform: "uppercase",
        fontFamily: "Helvetica-Bold"
    },
    personalDetails: {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        marginRight: 24
    },
    personalDetailsLeftBox: {
        width: "50%",
        marginLeft: 16,
        marginTop: 8
    },
    wrapper: {
        display: "flex",
        flexDirection: "row", // Added this to align label and answer side by side
        padding: 4,
    },
    label: {
        fontSize: 10,
        width: "35%", // Fixed portion
    },
    answer: {
        fontSize: 10,
        borderBottom: "1px solid #000000",
        width: "100%",
        paddingLeft:4,
        flexGrow: 1,// This makes it fill the remaining space
    },
    contactInfo: {
        marginTop: 20,
    },
    tableWrapper: {
        marginTop: 8,
        marginLeft: 20,
        marginRight: 16,
        width: "95%",
    },
    tableContent: {
        paddingLeft: 8,
        paddingTop: 4,
        paddingBottom: 4,
    },
    contactType: {
        maxWidth: 80
    },
    contactType2:{
        maxWidth: 120
    },
    familyDoctor: {
        maxWidth: 206,
        textAlign: "center",
        paddingLeft: 60
    },
    phoneType2: {
        maxWidth: 206,
        textAlign: "center",
        paddingLeft: 70
    },
    referringDoctor: {
        maxWidth: 206,
        textAlign: "center",
        paddingLeft: 60
    },
    phoneType: {
        maxWidth: 120,
        textAlign: "center",
        paddingLeft: 40
    },
    phoneTypeHeader: {
        maxWidth: 120,
        textAlign: "center",
        paddingLeft: 30
    },
    phoneNumber: {
        maxWidth: 130,
        paddingLeft: 30
    },
    address: {
        display: "flex",
        flexDirection: "row", // Added this to align label and answer side by side
    },
    addressLabel: {
        width: "12%",
        fontSize: 10,
        marginLeft: 16,
        paddingTop: 8

    },
    addressAnswer: {
        width: "80%",
        fontSize: 10,
        paddingTop: 8,
        borderBottom: "1px solid #000000",

    },
    employmentStatus: {
        fontSize: 10,
        width: "40%", // Fixed portion
    },
    employmentStatusAnswer: {
        fontSize: 10,
        borderBottom: "1px solid #000000",
        width: "60%",
    },
    workInfoDetailsLeftBox: {
        width: "50%",
        marginLeft: 12,
        marginTop: 8
    },
    occupation: {
        width: "25%",
    },
    occupationAnswer: {
        width: "75%",
    },
    employmentType: {
        marginRight: 14
    },
    emergencyDetailsContainer: {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        justifyContent: "space-between",
        fontSize: 10,
        marginLeft: 16
    },
    emergencyDetailsWrapper: {
        display: "flex",
        flexDirection: "row",
        marginTop: 8
    },
    value: {
        borderBottom: "1px solid #000000",
        width: "100%",
    },
    relationship: {
        marginLeft: 12
    },
    relationshipLabel: {
        marginRight: 16
    },
    footer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        position: 'absolute',
        bottom: 10,
        left: 24,
        right: 24,
        padding: 10,
        fontSize: 10,
        marginTop: 60
    },
    listItem: {
        fontSize: 10,
        paddingBottom: 4
    },
});

// Header Component - fixed at top
const Header = () => (
    <View style={styles.header} fixed>
        <Text>Medical History Questionnaire</Text>
    </View>
);

// Footer Component - fixed at bottom
const Footer = () => (
    <View style={styles.footer} fixed>
        <Text style={{fontSize: 10, fontStyle: "italic"}}>© Kinergy Sports Medicine & Performance. All Rights
            Reserved.</Text>
        <Text style={{fontSize: 10}} render={({pageNumber, totalPages}) => `Page ${pageNumber} / ${totalPages}`}/>
    </View>
);

const MedicalHistoryQuestionnairePdf = () => {
    const {medicalHistoryOptionsList} = useSelector((state: IRootReducerState) => state.staticData);
    const {clientMedicalHistoryDetails, clientBasicDetails} = useSelector((state: IRootReducerState) => state.client);
    const {surgicalHistoryOptionsList} = useSelector((state: IRootReducerState) => state.staticData);

    const formatHabitAnswer = (label: string, text: string) => {
        if (label.includes("Tobacco")) return `${text} cigarettes/day`;
        if (label.includes("Alcohol")) return `${text} drinks/week`;
        if (label.includes("Coffee")) return `${text} cups/day`;
        if (label.includes("Soda") || label.includes("Pop")) return `${text} cans/day`;
        return text; // fallback
    };

    return (
        <PDFViewer style={{width: '100%', height: '80vh'}}>
            <Document>
                {clientBasicDetails &&

                    <Page size={'LETTER'} wrap={true} style={styles.page}>
                        <Header/>
                        <View style={styles.contentContainer}>
                            <View>
                                <Text style={styles.heading}>Personal Details:</Text>
                            </View>
                            <View style={styles.personalDetails}>
                                <View style={styles.personalDetailsLeftBox}>
                                    <View style={styles.wrapper}>
                                        <Text style={styles.label}>Last Name</Text>
                                        <Text style={styles.answer}>{clientBasicDetails.last_name || "N/A"}</Text>
                                    </View>
                                    <View style={styles.wrapper}>
                                        <Text style={styles.label}>Date of Birth</Text>
                                        <Text
                                            style={styles.answer}>{moment(clientBasicDetails.dob).format('DD-MMM-YYYY') || "N/A"}</Text>
                                    </View>
                                    <View style={styles.wrapper}>
                                        <Text style={styles.label}>NickName</Text>
                                        <Text style={styles.answer}>{clientBasicDetails.nick_name || 'N/A'}</Text>
                                    </View>
                                </View>
                                <View style={styles.personalDetailsLeftBox}>
                                    <View style={styles.wrapper}>
                                        <Text style={styles.label}>First Name</Text>
                                        <Text style={styles.answer}>{clientBasicDetails.first_name || "N/A"}</Text>
                                    </View>
                                    <View style={styles.wrapper}>
                                        <Text style={styles.label}>Gender</Text>
                                        <Text
                                            style={styles.answer}>{clientBasicDetails?.gender_details?.title || "N/A"}</Text>
                                    </View>
                                    <View style={styles.wrapper}>
                                        <Text style={styles.label}>SSN</Text>
                                        <Text style={styles.answer}>{clientBasicDetails.ssn || "N/A"}</Text>
                                    </View>
                                </View>
                            </View>
                            <View>
                                <Text style={[styles.heading, styles.contactInfo]}>Contact Information:</Text>
                            </View>
                            <Table style={styles.tableWrapper}>
                                <TH style={{fontSize: 10}}>
                                    <TD style={[styles.tableContent, styles.contactType]}>Contact Type</TD>
                                    <TD style={[styles.tableContent, styles.phoneTypeHeader]}>Phone Type</TD>
                                    <TD style={[styles.tableContent, styles.phoneNumber]}>Phone Number</TD>
                                    <TD style={[styles.tableContent]}>Email</TD>
                                </TH>
                                <TR style={{fontSize: 10}}>
                                    <TD style={[styles.tableContent, styles.contactType]}>Primary:</TD>
                                    <TD style={[styles.tableContent, styles.phoneType]}>{CommonService.capitalizeFirstLetterAndRemoveUnderScore(clientBasicDetails.primary_contact_info.phone_type) || 'N/A'}</TD>
                                    <TD style={[styles.tableContent, styles.phoneNumber]}>{CommonService.formatPhoneNumber(clientBasicDetails.primary_contact_info?.phone) || 'N/A'}</TD>
                                    <TD style={styles.tableContent}>{clientBasicDetails?.primary_email || 'N/A'}</TD>
                                </TR>
                                {
                                    clientBasicDetails?.secondary_contact_info?.length > 0 && clientBasicDetails?.secondary_contact_info?.map((item: any, index: number) => {
                                        return (
                                            <TR style={{fontSize: 10}} key={index}>
                                                <TD style={[styles.tableContent, styles.contactType]}>Alternate:{index + 1}</TD>
                                                <TD style={[styles.tableContent, styles.phoneType]}>{CommonService.capitalizeFirstLetterAndRemoveUnderScore(item.phone_type)}</TD>
                                                <TD style={[styles.tableContent, styles.phoneNumber]}>{CommonService.formatPhoneNumber(item.phone) ||'N/A'}</TD>
                                                <TD style={styles.tableContent}>{item.email || 'N/A'}</TD>
                                            </TR>
                                        )
                                    })
                                }
                            </Table>
                            <View style={{marginBottom: 20}}>
                                <Text style={[styles.heading, styles.contactInfo]}>Address Information:</Text>
                                <View style={styles.address}>
                                    <Text style={styles.addressLabel}>Address Line</Text>
                                    <Text
                                        style={styles.addressAnswer}>
                                        {clientBasicDetails.address.address_line}, {clientBasicDetails.address.city}, {clientBasicDetails.address.state}, {clientBasicDetails.address.country} {clientBasicDetails.address.zip_code}
                                    </Text>
                                </View>
                            </View>
                            <View style={{marginBottom: 20}}>
                                <Text style={styles.heading}>Work Information:</Text>
                                <View style={styles.personalDetails}>
                                    <View style={styles.workInfoDetailsLeftBox}>
                                        <View style={styles.wrapper}>
                                            <Text style={[styles.employmentStatus, styles.occupation]}>Occupation</Text>
                                            <Text
                                                style={[styles.employmentStatusAnswer, styles.occupationAnswer]}>{clientBasicDetails.work_info.occupation || "N/A"}</Text>
                                        </View>
                                    </View>
                                    <View style={styles.personalDetailsLeftBox}>
                                        <View style={styles.wrapper}>
                                            <Text style={styles.employmentStatus}>Employment Status</Text>
                                            <Text
                                                style={[styles.employmentStatusAnswer, styles.employmentType]}>{clientBasicDetails.work_info.employment_status_details.title || "N/A"}</Text>
                                        </View>
                                    </View>
                                </View>
                            </View>
                            <View>
                                <Text style={styles.heading}>Emergency Contact Details:</Text>
                                <Text style={{
                                    fontSize: 10,
                                    fontFamily: "Helvetica-Bold",
                                    marginLeft: 16,
                                    marginTop: 8,
                                    textDecoration: 'underline'
                                }}>Primary Emergency Contact</Text>
                                <View style={styles.emergencyDetailsContainer}>
                                    <View style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        width: "40%",
                                        marginTop: 8
                                    }}>
                                        <Text style={styles.label}>Full Name</Text>
                                        <Text
                                            style={styles.value}>{clientBasicDetails.emergency_contact_info.primary_emergency.name || "N/A"} </Text>
                                    </View>
                                    <View style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        width: "37%",
                                        marginTop: 8
                                    }}>
                                        <Text style={{
                                            fontSize: 10,
                                            paddingRight: 60,
                                            paddingLeft: 20
                                        }}>Relationship</Text>
                                        <Text style={{
                                            borderBottom: "1px solid #000000",
                                            marginRight: 24, width: "100%"
                                        }}>{clientBasicDetails.emergency_contact_info.primary_emergency.relationship || "N/A"}</Text>
                                    </View>
                                    <View style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        width: "33%",
                                        marginTop: 8
                                    }}>
                                        <Text style={{fontSize: 10, paddingRight: 50}}>Language</Text>
                                        <Text style={{
                                            borderBottom: "1px solid #000000",
                                            width: "100%", marginRight: 24
                                        }}>{CommonService.capitalizeFirstLetterAndRemoveUnderScore(clientBasicDetails.emergency_contact_info.primary_emergency?.language) || "N/A"}</Text>

                                    </View>
                                </View>
                                <Table style={styles.tableWrapper}>
                                    <TH style={{fontSize: 10}}>
                                        <TD style={[styles.tableContent, styles.contactType]}>Contact Type</TD>
                                        <TD style={[styles.tableContent, styles.familyDoctor]}>Phone Type</TD>
                                        <TD style={{paddingLeft: 100}}>Phone Number</TD>
                                    </TH>
                                    <TR style={{fontSize: 10}}>
                                        <TD style={[styles.tableContent, styles.contactType]}>Primary:</TD>
                                        <TD style={[styles.tableContent, styles.phoneType2]}>{CommonService.capitalizeFirstLetterAndRemoveUnderScore(clientBasicDetails.emergency_contact_info.primary_emergency?.primary_contact_info.phone_type) || "N/A"}</TD>
                                        <TD style={{paddingLeft: 100}}>{CommonService.formatPhoneNumber(clientBasicDetails.emergency_contact_info.primary_emergency?.primary_contact_info?.phone)}</TD>
                                    </TR>
                                    {
                                        clientBasicDetails?.emergency_contact_info?.primary_emergency?.secondary_contact_info?.length > 0 && clientBasicDetails?.emergency_contact_info?.primary_emergency?.secondary_contact_info?.map((item: any, index: number) => {
                                            return (
                                                <TR style={{fontSize: 10}} key={index}>
                                                    <TD style={[styles.tableContent, styles.contactType]}>Alternate:{index + 1}</TD>
                                                    <TD style={[styles.tableContent, styles.phoneType2]}>{CommonService.capitalizeFirstLetterAndRemoveUnderScore(item.phone_type) || "N/A"}</TD>
                                                    <TD style={{paddingLeft: 100}}>{CommonService.formatPhoneNumber(item.phone) || "N/A"}</TD>
                                                </TR>
                                            )
                                        })
                                    }
                                </Table>

                                {/*Second Emergency Contact*/}
                                <Text style={{
                                    fontSize: 10,
                                    fontFamily: "Helvetica-Bold",
                                    marginLeft: 16,
                                    marginTop: 8,
                                    textDecoration: 'underline'
                                }}>Secondary Emergency Contact</Text>
                                <View style={styles.emergencyDetailsContainer}>
                                    <View style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        width: "40%",
                                        marginTop: 8
                                    }}>
                                        <Text style={styles.label}>Full Name</Text>
                                        <Text style={styles.value}>{clientBasicDetails.emergency_contact_info?.secondary_emergency?.name || "N/A"}</Text>
                                    </View>
                                    <View style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        width: "37%",
                                        marginTop: 8
                                    }}>
                                        <Text style={{
                                            fontSize: 10,
                                            paddingRight: 60,
                                            paddingLeft: 20
                                        }}>Relationship</Text>
                                        <Text style={{
                                            borderBottom: "1px solid #000000",
                                            marginRight: 24, width: "100%"
                                        }}> {clientBasicDetails?.emergency_contact_info?.secondary_emergency?.relationship || "N/A"}</Text>
                                    </View>
                                    <View style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        width: "33%",
                                        marginTop: 8
                                    }}>
                                        <Text style={{fontSize: 10, paddingRight: 50}}>Language</Text>
                                        <Text style={{
                                            borderBottom: "1px solid #000000",
                                            width: "100%", marginRight: 24
                                        }}>{CommonService.capitalizeFirstLetterAndRemoveUnderScore(clientBasicDetails?.emergency_contact_info?.secondary_emergency?.language) || "N/A"}</Text>

                                    </View>
                                </View>
                                <Table style={styles.tableWrapper}>
                                    <TH style={{fontSize: 10}}>
                                        <TD style={[styles.tableContent, styles.contactType]}>Contact Type</TD>
                                        <TD style={[styles.tableContent, styles.familyDoctor]}>Phone Type</TD>
                                        <TD style={{paddingLeft: 100}}>Phone Number</TD>
                                    </TH>
                                    <TR style={{fontSize: 10}}>
                                        <TD style={[styles.tableContent, styles.contactType]}>Primary:</TD>
                                        <TD style={[styles.tableContent, styles.phoneType2]}>{CommonService.capitalizeFirstLetterAndRemoveUnderScore(clientBasicDetails?.emergency_contact_info?.secondary_emergency?.primary_contact_info.phone_type) || "N/A"}</TD>
                                        <TD style={{paddingLeft: 100}}>{CommonService.formatPhoneNumber(clientBasicDetails?.emergency_contact_info?.secondary_emergency?.primary_contact_info.phone) || "N/A"}</TD>
                                    </TR>
                                    {
                                        clientBasicDetails?.emergency_contact_info?.secondary_emergency?.secondary_contact_info?.map((item: any, index: number) => {
                                            return (
                                                <TR style={{fontSize: 10}} key={index}>
                                                    <TD style={[styles.tableContent, styles.contactType]}>Alternate:{index + 1}</TD>
                                                    <TD style={[styles.tableContent, styles.phoneType2]}>{CommonService.capitalizeFirstLetterAndRemoveUnderScore(item.phone_type) || "N/A"}</TD>
                                                    <TD style={{paddingLeft: 100}}>{CommonService.formatPhoneNumber(item.phone) || "N/A"}</TD>
                                                </TR>
                                            )
                                        })
                                    }
                                </Table>
                            </View>
                            {/*Personal Habits   */}
                            <View style={{marginTop: 20}}>
                                <Text style={styles.heading}>Personal Habits:</Text>
                            </View>
                            <View style={styles.personalDetails}>
                                <View style={styles.personalDetailsLeftBox}>
                                    {clientMedicalHistoryDetails?.personal_habits && Object?.entries(clientMedicalHistoryDetails?.personal_habits)?.slice(0, 2)?.map(([label, data]:any) => (
                                        <View style={styles.wrapper} key={label}>
                                            <Text style={{ fontSize: 10, width: "70%" }}>{label}</Text>
                                            <Text style={styles.answer}>
                                                {data.value === "Yes" ? formatHabitAnswer(label, data.text) : "No"}
                                            </Text>
                                        </View>
                                    ))}
                                </View>
                                <View style={styles.personalDetailsLeftBox}>
                                    {clientMedicalHistoryDetails?.personal_habits && Object?.entries(clientMedicalHistoryDetails?.personal_habits)?.slice(2)?.map(([label, data]:any) => (
                                        <View style={styles.wrapper} key={label}>
                                            <Text style={{ fontSize: 10, width: "70%" }}>{label}</Text>
                                            <Text style={styles.answer}>
                                                {data.value === "Yes" ? formatHabitAnswer(label, data.text) : "No"}
                                            </Text>
                                        </View>
                                    ))}
                                </View>
                            </View>

                            {/*Allergies*/}
                            <View style={{marginTop: 20}}>
                                <Text style={styles.heading}>Allergies:</Text>
                            </View>
                            <View style={{
                                border: "1px solid #000000",
                                paddingTop: 8,
                                paddingLeft: 20,
                                paddingRight: 20,
                                paddingBottom: 8,
                                marginLeft: 20,
                                marginTop: 8,
                                marginRight: 10
                            }}>
                                <View style={{fontSize: 10}}>
                                    <View>{clientMedicalHistoryDetails?.allergies?.split("\n").map((i: any, key: any) => {
                                        return <Text key={key}>• {i}</Text>;
                                    }) || 'N/A'}</View>
                                </View>
                            </View>
                            {/*Medications / Supplements*/}
                            <View style={{marginTop: 20}}>
                                <Text style={styles.heading}>MEDICATIONS / SUPPLEMENTS:</Text>
                            </View>
                            <View style={{
                                border: "1px solid #000000",
                                paddingTop: 8,
                                paddingLeft: 8,
                                paddingRight: 8,
                                paddingBottom: 8,
                                marginLeft: 20,
                                marginTop: 8,
                                marginRight: 10,

                            }}>
                                <View>
                                    <Text
                                        style={{
                                            textDecoration: 'underline',
                                            fontSize: 10,
                                            paddingBottom: 4,
                                            marginBottom: 4,
                                        }}
                                    >
                                        Prescription Medications:
                                    </Text>
                                    <Text style={{ fontSize: 10, paddingLeft: 10 }}>
                                        • {clientMedicalHistoryDetails?.medications?.prescription_medication?.trim() || "None"}
                                    </Text>
                                </View>

                                <View style={{ marginTop: 10 }}>
                                    <Text
                                        style={{
                                            textDecoration: 'underline',
                                            fontSize: 10,
                                            paddingBottom: 4,
                                            marginBottom: 4,
                                        }}
                                    >
                                        Non-Prescription Medications / Supplements:
                                    </Text>
                                    <Text style={{ fontSize: 10, paddingLeft: 10 }}>
                                        • {clientMedicalHistoryDetails?.medications?.non_prescription_medication?.trim() || "None"}
                                    </Text>
                                </View>
                            </View>
                            {/*Medical provider information:*/}
                            <View style={{marginTop: 20}}>
                                <Text style={styles.heading}>Medical Provider Information:</Text>
                            </View>
                            <Table style={styles.tableWrapper}>
                                <TH style={{fontSize: 10}}>
                                    <TD style={[styles.tableContent, styles.contactType]}>Description</TD>
                                    <TD style={[styles.tableContent, styles.familyDoctor]}>Family Doctor</TD>
                                    <TD style={{paddingLeft: 80}}>Referring Doctor</TD>
                                </TH>
                                <TR style={{fontSize: 10}}>
                                    <TD style={[styles.tableContent, styles.contactType]}>Full Name:</TD>
                                    <TD style={[styles.tableContent, styles.familyDoctor]}>{clientMedicalHistoryDetails?.medical_provider_info?.family_doctor_name || "N/A"}</TD>
                                    <TD style={{paddingLeft: 100}}>{clientMedicalHistoryDetails?.medical_provider_info?.referring_doctor_name || "N/A"}</TD>
                                </TR>
                                <TR style={{fontSize: 10}}>
                                    <TD style={[styles.tableContent, styles.contactType]}>MD Phone:</TD>
                                    <TD style={[styles.tableContent, styles.familyDoctor]}>{CommonService.formatPhoneNumber(clientMedicalHistoryDetails?.medical_provider_info?.primary_phone) || "N/A"}</TD>
                                    <TD style={{paddingLeft: 80}}>{CommonService.formatPhoneNumber(clientMedicalHistoryDetails?.medical_provider_info?.md_phone) || "N/A"}</TD>
                                </TR>
                            </Table>
                            <View style={{display: 'flex', flexDirection: 'row'}}>
                                <Text style={{fontSize: 10, marginTop: 8, marginLeft: 19}}>
                                    Date of Last Physical Examination:
                                </Text>
                                <Text style={{
                                    fontSize: 10,
                                    marginTop: 8,
                                    marginLeft: 5,
                                    borderBottom: "1px solid #000000",
                                    paddingLeft: 4
                                }}>{CommonService.getDateFromTimezone(clientMedicalHistoryDetails?.medical_provider_info?.last_examination_date, moment.tz.guess(), "DD-MMM-YYYY")}</Text>
                            </View>
                        </View>
                        <View break/>
                        <MedicalHistoryPdf medicalHistoryOptionsList={medicalHistoryOptionsList}
                                           clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                        />
                        <SurgicalHistoryPdf clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                                            surgicalHistoryOptionsList={surgicalHistoryOptionsList}
                                            clientBasicDetails={clientBasicDetails}
                        />

                        <Footer/>
                    </Page>
                }
            </Document>
        </PDFViewer>
    )
}

export default MedicalHistoryQuestionnairePdf;

//<View break /> it is used to change the page if you want to add a page break
